import Loader from '@/components/Loader';
import { trpc } from '@/trpcClient';
import { cn } from '@/utils/utils';
import { HierarchyNode } from '@shelflix/trpc/routes/user/hierarchy/query';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { toast } from 'sonner';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shelflix/ui-web/components/ui/collapsible';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ChevronRight, Crown, Eye, GripVertical, Shield, TrendingUp, Users, User } from 'lucide-react';

type HierarchyTreeNode = HierarchyNode;

type RemoveNodeResult = {
  tree: HierarchyTreeNode[];
  removedNode: HierarchyTreeNode | null;
  changed: boolean;
};

function removeNodeFromTree(nodes: HierarchyTreeNode[], userUuid: string): RemoveNodeResult {
  let removedNode: HierarchyTreeNode | null = null;
  let changed = false;

  const tree = nodes.reduce<HierarchyTreeNode[]>((accumulator, node) => {
    if (node.user.uuid === userUuid) {
      removedNode = node;
      changed = true;
      return accumulator;
    }

    const result = removeNodeFromTree(node.subordinates, userUuid);
    if (result.removedNode) {
      removedNode = result.removedNode;
    }

    if (result.changed) {
      changed = true;
      accumulator.push({
        ...node,
        subordinates: result.tree,
      });
      return accumulator;
    }

    accumulator.push(node);
    return accumulator;
  }, []);

  return {
    tree,
    removedNode,
    changed,
  };
}

function findNodeByUuid(nodes: HierarchyTreeNode[], userUuid: string): HierarchyTreeNode | null {
  for (const node of nodes) {
    if (node.user.uuid === userUuid) {
      return node;
    }

    const found = findNodeByUuid(node.subordinates, userUuid);
    if (found) {
      return found;
    }
  }

  return null;
}

function isDescendantOf(nodes: HierarchyTreeNode[], ancestorUuid: string, candidateUuid: string): boolean {
  const ancestor = findNodeByUuid(nodes, ancestorUuid);

  if (!ancestor) {
    return false;
  }

  return Boolean(findNodeByUuid(ancestor.subordinates, candidateUuid));
}

function insertNodeIntoTree(
  nodes: HierarchyTreeNode[],
  targetUuid: string | null,
  nodeToInsert: HierarchyTreeNode
): HierarchyTreeNode[] {
  if (targetUuid === null) {
    return [...nodes, nodeToInsert];
  }

  return nodes.map((node) => {
    if (node.user.uuid === targetUuid) {
      return {
        ...node,
        subordinates: [...node.subordinates, nodeToInsert],
      };
    }

    const nextSubordinates = insertNodeIntoTree(node.subordinates, targetUuid, nodeToInsert);
    if (nextSubordinates !== node.subordinates) {
      return {
        ...node,
        subordinates: nextSubordinates,
      };
    }

    return node;
  });
}

function countNodes(nodes: HierarchyTreeNode[]): number {
  return nodes.reduce((accumulator, node) => accumulator + 1 + countNodes(node.subordinates), 0);
}

export function UserHierarchy() {
  const {
    data: hierarchyData,
    isLoading,
    error,
  } = useQuery({
    ...trpc.user.userHierarchy.queryOptions(),
  });

  const editUserMutation = useMutation(trpc.user.edit.mutationOptions());

  const [draggedUserUuid, setDraggedUserUuid] = useState<string | null>(null);
  const [dropTargetUuid, setDropTargetUuid] = useState<string | null>(null);
  const [tree, setTree] = useState<HierarchyTreeNode[]>(hierarchyData?.hierarchy || []);

  useEffect(() => {
    if (hierarchyData?.hierarchy) {
      setTree(hierarchyData.hierarchy);
    }
  }, [hierarchyData?.hierarchy]);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-red-600">Erro ao carregar a hierarquia: {error.message}</p>
      </div>
    );
  }

  const hierarchy = tree;
  const totalUsers = countNodes(hierarchy);
  const roleCounts = hierarchy.reduce(
    (acc, node) => {
      const countRoles = (n: HierarchyNode) => {
        acc[n.user.roleName] = (acc[n.user.roleName] || 0) + 1;
        n.subordinates.forEach(countRoles);
      };
      countRoles(node);
      return acc;
    },
    {} as Record<string, number>
  );

  const getRoleIcon = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin':
        return <Crown className="h-4 w-4 text-[var(--primary)]" />;
      case 'gerente':
        return <Shield className="h-4 w-4 text-[var(--primary)]" />;
      case 'supervisor':
        return <Eye className="h-4 w-4 text-[var(--primary)]" />;
      case 'promotor':
        return <User className="h-4 w-4 text-[var(--gray-primary)]" />;
      default:
        return <User className="h-4 w-4 text-[var(--gray-secondary)]" />;
    }
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'gerente':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'supervisor':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'promotor':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleDragStart = (userUuid: string) => {
    setTimeout(() => {
      setDraggedUserUuid(userUuid);
    }, 0);
  };

  const handleDragEnd = () => {
    setDraggedUserUuid(null);
    setDropTargetUuid(null);
  };

  const handleMoveUser = (draggedUserUuid: string, targetUserUuid: string | null) => {
    if (draggedUserUuid === targetUserUuid) {
      return;
    }

    if (targetUserUuid !== null && isDescendantOf(hierarchy, draggedUserUuid, targetUserUuid)) {
      toast.error('Não é possível mover um usuário para dentro de sua própria subárvore.');
      return;
    }

    const removal = removeNodeFromTree(hierarchy, draggedUserUuid);
    if (!removal.removedNode) {
      return;
    }

    const updatedNode: HierarchyTreeNode = {
      ...removal.removedNode,
      user: {
        ...removal.removedNode.user,
        responsibleUserId: targetUserUuid ? (findNodeByUuid(hierarchy, targetUserUuid)?.user.id ?? null) : null,
        responsibleUserName: targetUserUuid ? (findNodeByUuid(hierarchy, targetUserUuid)?.user.name ?? null) : null,
      },
    };

    const responsibleUser =
      updatedNode.user.roleId === 1 ? '00000000-0000-4000-8000-000000000000' : (targetUserUuid ?? null);

    if (responsibleUser === null) {
      toast.error('Não é possível mover o usuário: apenas admin pode ficar sem responsável.');
      return;
    }

    const nextTree = insertNodeIntoTree(removal.tree, targetUserUuid, updatedNode);

    setTree(nextTree);

    editUserMutation.mutate({
      uuid: updatedNode.user.uuid,
      name: updatedNode.user.name,
      phone: updatedNode.user.phone,
      email: updatedNode.user.email,
      role: updatedNode.user.roleUuid,
      active: updatedNode.user.active,
      responsibleUser: responsibleUser,
    });
  };

  function Tree({ node }: { node: HierarchyNode }) {
    const { user, subordinates } = node;
    const hasSubordinates = subordinates.length > 0;
    const isDragged = draggedUserUuid === user.uuid;
    const isDropTarget = dropTargetUuid === user.uuid;
    const isDragging = draggedUserUuid !== null;
    const [isOpen, setOpen] = useState(true);

    return (
      <div className="ml-4">
        <Collapsible
          className="[&[data-state=open]>div>button>div>div>svg:nth-child(2)]:rotate-90"
          open={isOpen}
          onOpenChange={(open) => {
            if (isDragging) return;
            setOpen(open);
          }}
        >
          <div
            draggable
            onDragStart={() => handleDragStart(user.uuid)}
            onDragEnd={handleDragEnd}
            onDragOver={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDropTargetUuid(user.uuid);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              // event.stopPropagation();
              setDropTargetUuid((currentTarget) => (currentTarget === user.uuid ? null : currentTarget));
            }}
            onDrop={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (draggedUserUuid !== null) {
                handleMoveUser(draggedUserUuid, user.uuid);
              }
              handleDragEnd();
            }}
            className={cn(
              'rounded-[10px] border border-[var(--border)] transition-colors',
              isDragged && 'opacity-40',
              isDropTarget && 'border-[var(--primary)] bg-[var(--secondary)]/40'
            )}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  'h-auto w-full justify-start rounded-[10px] px-4 py-3',
                  isDragged && 'hover:!bg-transparent'
                )}
              >
                <div className="flex w-full items-center gap-3">
                  <div className="flex items-center gap-1.5 text-[var(--gray-secondary)]">
                    <GripVertical className="h-4 w-4" />
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 transition-transform',
                        hasSubordinates ? 'text-[var(--gray-primary)]' : 'text-transparent'
                      )}
                    />
                  </div>
                  <div className="rounded-full bg-[var(--secondary)] p-2">{getRoleIcon(user.roleName)}</div>
                  <div className="flex-1 text-left">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-[var(--gray-primary)]">{user.name}</span>
                      {user.isMaster && <Crown className="h-4 w-4 text-[var(--primary)]" />}
                    </div>
                    <p className="text-sm text-[var(--gray-secondary)]">{user.email}</p>
                  </div>
                  <div className="items-center gap-2 lg:flex">
                    <Badge className={`${getRoleColor(user.roleName)} text-xs`}>{user.roleName}</Badge>
                    {!user.active && (
                      <Badge variant="destructive" className="text-xs">
                        Inativo
                      </Badge>
                    )}
                    {hasSubordinates && (
                      <Badge variant="outline" className="text-xs">
                        {subordinates.length} subordinado{subordinates.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </Button>
            </CollapsibleTrigger>
          </div>
          {hasSubordinates && (
            <CollapsibleContent>
              <div className="mt-2 ml-4 space-y-1">
                {subordinates.map((subordinate) => (
                  <Tree key={subordinate.user.id} node={subordinate} />
                ))}
              </div>
            </CollapsibleContent>
          )}
        </Collapsible>
      </div>
    );
  }

  function HierarchyTree({ hierarchy }: { hierarchy: HierarchyNode[] }) {
    if (hierarchy.length === 0) {
      return (
        <div className="rounded-[10px] border border-dashed border-[var(--border)] bg-[var(--secondary)]/30 py-10 text-center">
          <Users className="mx-auto mb-4 h-12 w-12 text-[var(--gray-secondary)]" />
          <p className="text-[var(--gray-secondary)]">Nenhuma hierarquia encontrada</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {hierarchy.map((node) => (
          <Tree key={node.user.id} node={node} />
        ))}
      </div>
    );
  }

  const handleRootDrop = () => {
    if (draggedUserUuid !== null) {
      handleMoveUser(draggedUserUuid, null);
    }
    handleDragEnd();
  };

  return (
    <div className="space-y-6">
      <Card className="border border-[var(--border)] shadow-none">
        <CardHeader className="space-y-3 border-b border-[var(--border)] pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-[10px] bg-[var(--secondary)] p-2 text-[var(--primary)]">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-[18px] text-[var(--gray-primary)]">Hierarquia de Cargos</CardTitle>
              <p className="mt-1 text-sm text-[var(--gray-secondary)]">
                Arraste um usuário sobre outro para alterar a relação de responsável.
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-[10px] border border-[var(--border)] px-4 py-3">
              <p className="text-xs tracking-wide text-[var(--gray-secondary)] uppercase">Usuários totais</p>
              <p className="mt-1 text-lg font-semibold text-[var(--gray-primary)]">{totalUsers}</p>
            </div>
            {Object.entries(roleCounts).map(([role, count]) => (
              <div className="rounded-[10px] border border-[var(--border)] px-4 py-3">
                <div className="flex items-center gap-2">
                  <p className="text-xs tracking-wide text-[var(--gray-secondary)] uppercase">{role}</p>
                  {role.toLowerCase() === 'admin' && <Crown className="h-4 w-4 text-[var(--primary)]" />}
                  {role.toLowerCase() === 'gerente' && <Shield className="h-4 w-4 text-[var(--primary)]" />}
                  {role.toLowerCase() === 'supervisor' && <Eye className="h-4 w-4 text-[var(--primary)]" />}
                  {role.toLowerCase() === 'promotor' && <User className="h-4 w-4 text-[var(--gray-primary)]" />}
                  {role.toLowerCase() === 'coordenador/executivo/especialista' && (
                    <User className="h-4 w-4 text-[var(--gray-secondary)]" />
                  )}
                </div>
                <p className="mt-1 text-lg font-semibold text-[var(--gray-primary)]">{count}</p>
              </div>
            ))}
          </div>
        </CardHeader>

        <CardContent
          className="space-y-4 p-4 md:p-6"
          onDragOver={(event) => {
            event.preventDefault();
            setDropTargetUuid(null);
          }}
          onDrop={(event) => {
            event.preventDefault();
            handleRootDrop();
          }}
        >
          <HierarchyTree hierarchy={hierarchy} />
        </CardContent>
      </Card>
    </div>
  );
}
